import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import { endpoints } from "../../hooks/endpoints";
import { getToken } from "../../hooks/utils";
import Animation from "../Animation";
import NotFound from "../NotFound";
import Spinner from "../Spinner";
import QuestionImg from "./QuestionImg";

interface GradeQuestionI {
  id: number;
  content: string;
  img: string;
  weight: number;
  answers: string[];
  correct_answer: string;
  student_answer: string;
  correct: boolean;
}

interface GradeI {
  id: number;
  date: string;
  raw_data: string[];
  questions: GradeQuestionI[];
  hide_correct_answers: boolean;
}

const Grade = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const fetchGrade = async () => {
    const response = await fetch(`${endpoints.getGrade}?grade=${id}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: getToken(),
      },
      method: "GET",
    });

    if (!response.ok) throw new Error("حدث خطأ");

    const res = await response.json();

    if (!res?.raw_data[0]) throw new Error("حدث خطأ");

    return res as GradeI;
  };

  const {
    data: grade,
    isPending,
    error,
  } = useQuery({
    queryKey: ["grade", id],
    queryFn: fetchGrade,
    staleTime: 5 * 60 * 1000,
  });

  if (error) return <NotFound />;

  if (isPending)
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner variant="default" />
      </div>
    );

  return (
    <Animation>
      <div>
        <div className="responsive-grade mx-auto py-3">
          {grade && (
            <div className="p-3">
              <div className="flex">
                <img
                  className="image-fluid w-1/2 mx-auto"
                  src="https://drive.google.com/uc?id=11oWusZr8VM7oYq5axs4k4_WSJqcie-s2"
                  alt=""
                />
              </div>
              <div className="flex mb-3">
                <h3 className="m-auto">درجتك هي</h3>
              </div>
              <p className="font-bold my-auto text-xl m-2 text-center">
                {grade.raw_data
                  ? `${JSON.parse(grade.raw_data[0]).score} من ${
                      JSON.parse(grade.raw_data[0]).maxScore
                    }`
                  : ""}
              </p>
              <div
                className="progress rounded-xl responsive-quiz-grade mx-auto mt-2"
                style={{ height: ".75rem", backgroundColor: "#ddd" }}
              >
                <div
                  className="progress-bar rounded-xl"
                  role="progressbar"
                  style={{
                    width: grade.raw_data
                      ? `${
                          (JSON.parse(grade.raw_data[0]).score /
                            JSON.parse(grade.raw_data[0]).maxScore) *
                          100
                        }%`
                      : "0px",
                    height: ".75rem",
                  }}
                  aria-valuenow={
                    grade.raw_data
                      ? (JSON.parse(grade.raw_data[0]).score /
                          JSON.parse(grade.raw_data[0]).maxScore) *
                        100
                      : 0
                  }
                  aria-valuemin={0}
                  aria-valuemax={100}
                ></div>
              </div>
            </div>
          )}
          {grade &&
            grade.questions?.map((question: GradeQuestionI) => (
              <div
                key={question.id}
                className={`p-2 my-3 bg-white mx-4 border border-(--primary-color-light) rounded-xl quiz-ended`}
              >
                <div className="rounded-xl flex">
                  {question && <QuestionImg src={question.img} alt="" />}
                </div>
                {question.content ? (
                  <>
                    {question.content.indexOf("\n") !== 1 ? (
                      question.content
                        .split("\n")
                        .map((p: string) => <p>{p}</p>)
                    ) : (
                      <p>{question.content}</p>
                    )}
                  </>
                ) : (
                  ""
                )}
                <div
                  className="my-2 w-full space-y-2"
                  role="group"
                  aria-label="Basic radio toggle button group"
                >
                  {question.answers.map((answer) => (
                    <div className="flex">
                      <input
                        type="radio"
                        className="btn-check"
                        name={String(question.id)}
                        id={`btnradio-${question.id}-${answer}`}
                        value={answer}
                        autoComplete="off"
                        checked={
                          question.student_answer === answer ? true : null
                        }
                        hidden
                        // required={lessonData.must_answer_all === 'on' ? true : false}
                      />
                      <label
                        className="w-full rounded-xl py-2 px-4 border border-primary font-bold"
                        htmlFor={`btnradio-${question.id}-${answer}`}
                      >
                        {answer}
                      </label>
                    </div>
                  ))}
                </div>
                <div className="flex">
                  <div className="flex w-3/4 mx-auto">
                    <div className="mx-auto flex flex-wrap">
                      {question.correct && (
                        <div className="flex m-2">
                          <div
                            style={{ maxHeight: "50px" }}
                            className="flex py-2 px-4 rounded-xl border border-green-400 bg-green-50 text-green-600 mx-auto"
                            role="alert"
                          >
                            <p className="font-bold m-auto">إجابة صحيحة</p>
                          </div>
                        </div>
                      )}
                      {!question.correct && (
                        <div className="flex m-2">
                          <div
                            style={{ maxHeight: "50px" }}
                            className="d-flex py-2 px-4 rounded-xl border border-red-400 bg-red-50 text-red-600 mx-auto"
                            role="alert"
                          >
                            {grade.hide_correct_answers ? (
                              <p className="font-bold m-auto">إجابة خاطئة</p>
                            ) : (
                              <p className="font-bold m-auto">{`الإجابة الصحيحة هي ${question.correct_answer}`}</p>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="flex m-2">
                        <div
                          style={{ maxHeight: "50px" }}
                          className="flex py-2 px-4 rounded-xl border border-yellow-400 bg-yellow-50 text-yellow-600 mx-auto"
                          role="alert"
                        >
                          <p className="m-auto font-bold">
                            الدرجة: {question.weight}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </Animation>
  );
};

export default Grade;
